import React, { useState } from 'react';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const DeleteInvoiceModal = ({ isOpen, onClose, invoice, onDeleted }) => {
  const [reason, setReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!reason.trim()) {
      toast.error('Powód usunięcia jest wymagany');
      return;
    }

    setIsDeleting(true);
    try {
      await api.delete(`/invoices/${invoice.id}`, {
        data: { reason: reason.trim() }
      });
      toast.success('Faktura została usunięta');
      onDeleted();
      onClose();
      setReason('');
    } catch (error) {
      console.error('Błąd podczas usuwania faktury:', error);
      const errorMessage = error.response?.data?.error || 'Nie udało się usunąć faktury';
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Usuń fakturę</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium mb-1">Uwaga!</p>
              <p>Faktura zostanie przeniesiona do kosza. Operacja jest nieodwracalna.</p>
            </div>
          </div>

          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              <span className="font-medium">Numer faktury:</span> {invoice.number}
            </p>
            {invoice.supplierName && (
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">Dostawca:</span> {invoice.supplierName}
              </p>
            )}
          </div>

          <div className="mb-6">
            <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
              Powód usunięcia <span className="text-red-500">*</span>
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Wpisz powód usunięcia faktury..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Powód jest wymagany i będzie widoczny w historii faktury.
            </p>
          </div>

          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anuluj
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting || !reason.trim()}
              className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Usuwanie...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Usuń fakturę
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteInvoiceModal;
